import { theme, mediaMax, media } from "@customer-tools-web/component-ui";
import { Badge } from "antd";
import { BadgeProps } from "antd/lib/badge";
import { LocationDescriptorObject } from "history";
import { omit } from "lodash";
import * as React from "react";
import { Link as DomLink } from "react-router-dom";
import styled, { css } from "styled-components";
interface LinkProps {
  itemLink: string;
  itemRouterOptions?: LocationDescriptorObject;
  "data-test"?: string;
  itemLabel?: React.ReactNode;
  shouldUseRouter?: boolean;
  className?: string;
  isGrey?: boolean;
  openInSameTab?: boolean;
}

interface NavigatorLinkButtonProps {
  isPrimary?: boolean;
  height?: number;
  isGrey?: boolean;
}

const Link = React.memo((props: LinkProps) => {
  const { itemLink, itemLabel, itemRouterOptions, className, openInSameTab } =
    props;
  const isExternalLink = /^http.+/.test(itemLink);
  return props.shouldUseRouter ? (
    <DomLink
      className={className}
      to={itemRouterOptions || itemLink}
      data-test={props["data-test"]}
    >
      {itemLabel}
    </DomLink>
  ) : (
    <a
      href={itemLink}
      className={className}
      target={isExternalLink && !openInSameTab ? "_blank" : "_self"}
      data-test={props["data-test"]}
    >
      {itemLabel}
    </a>
  );
});

const NavigatorLink = React.memo(
  (props: LinkProps & { dataTest?: string; onClick?: () => void }) => {
    const { onClick, dataTest } = props;
    return onClick ? (
      <TextButton
        {...omit(props, ["itemLink", "itemRouterOptions", "dataTest"])}
        onClick={onClick}
        data-test={dataTest}
        hasLink={!!props.itemLink}
        className="submenu-item-title-text"
      >
        {props.itemLink ? (
          <Link {...omit(props, ["dataTest", "onClick"])} />
        ) : (
          props.itemLabel
        )}
      </TextButton>
    ) : (
      <Link {...props} data-test={dataTest} />
    );
  }
);

const TextButton = styled.div<{ hasLink: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  ${(props) =>
    !props.hasLink &&
    css`
      cursor: default;
      & + i {
        cursor: default;
      }
    `}
`;

const LinkButton = styled(NavigatorLink)<NavigatorLinkButtonProps>`
  display: block;
  font-weight: 600;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  line-height: ${(props) => props.height || 40}px;
  height: ${(props) => props.height || 40}px;
  letter-spacing: 0;
  text-align: center;
  border: 1px solid
    ${({ isGrey }) => (isGrey ? theme.textColor : theme.primary)};
  border-radius: 3px;
  color: ${({ theme, isPrimary, isGrey }) => {
    if (isGrey) return theme.textColor;
    return isPrimary ? theme.white : theme.blue;
  }};
  background-color: ${({ theme, isPrimary }) =>
    isPrimary ? theme.primary : theme.white};
  :hover {
    background-color: ${({ theme, isPrimary }) =>
      isPrimary ? theme.primaryDeep : theme.white};
    color: ${({ theme, isGrey }) => isGrey && theme.primary};
    border-color: ${({ theme, isGrey }) => isGrey && theme.primary};
  }
`;

const LabelWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 200px;
  width: 100%;
`;

const BetaTag = styled.div`
  background: #008549;
  margin-left: auto;
  padding: 4px 8px;
  min-width: 47px;
  max-height: 25px;
  border-radius: 8px;

  color: #ffffff;
  text-align: center;
  line-height: 16.8px;
  font-size: 12px;
  font-weight: 400;
`;

const CounterTag = styled.div`
  background: #0c59bc;
  margin: 0px 8px;
  padding: 0px 2px;
  min-width: 16px;
  max-width: 34px;
  max-height: 16px;
  border-radius: 8px;

  color: #eff1f5;
  text-align: center;
  line-height: 16.8px;
  font-size: 12px;
  font-weight: 600;
`;

const NotificationBadge = styled(Badge)<BadgeProps>`
  position: absolute;
`;

const MenuTag = styled.div<{
  pattern?: number;
}>`
  position: absolute;
  background: #a83399;
  border-radius: 10px;
  color: ${(props) => props?.theme?.white};
  font-weight: 600;
  font-size: 8px;
  line-height: 9px;
  padding: 4px;
  right: 0px;
  bottom: 31px;

  ${mediaMax.md`
    right: 0px;
  `};

  /* for listings */
  ${({ pattern }) =>
    (pattern === 1 || pattern === 2) &&
    css`
      /* for mobile & tablet */
      ${mediaMax.md`
        position: unset;
        left: unset;
        right: unset;
        bottom: unset;
        margin-left: 4px;
    `};

      /* for desktop */
      ${media.lg`
        ${({ pattern }: { pattern: number }) =>
          pattern === 1 &&
          css`
            right: 0px;
            bottom: 31px;
            text-align: center;
          `}
      
      /* for submenu */
       ${({ pattern }: { pattern: number }) =>
         pattern === 2 &&
         css`
           position: unset;
           left: unset;
           right: unset;
           bottom: unset;
           margin-left: 4px;
         `}
      `};
    `}

  /* for LEADS */
  ${({ pattern }) => pattern === 3 && menuTagPatternStyles};
`;

const menuTagPatternStyles = css`
  /* for mobile & tablet */
  ${mediaMax.md`
      left: 62px;
      bottom: 20px;
  `};
`;

const TooltipContentWrapper = styled.div`
  .title {
    font-weight: 600;
    font-size: 18px;
    line-height: 22px;
  }

  .content {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin-top: 8px;
  }
`;

export {
  LinkButton,
  NavigatorLink,
  Link,
  TextButton,
  BetaTag,
  CounterTag,
  MenuTag,
  NotificationBadge,
  TooltipContentWrapper,
  LabelWrap,
};
